import { EMOTES, ITEMS, randomLook, type Look, type Slot } from "../shared/catalog";
import { blockedSet, MAX_GRID, STEP_MS, toFurni, type Furni, type Placed } from "../shared/protocol";
import { drawAvatar } from "./avatar";
import { drawInDepthOrder, furniBox, personBox, type Drawable } from "./depth";
import { drawFloorAndWalls, drawFurni, furniDepth, isFlat, iso } from "./room";
import { authHeaders, getSession, getToken, signInWithX } from "./token";

// ---------- the demo room: a little crew hanging out in a decked-out room ----------

const SIZE = 7;
const DEMO: Omit<Placed, "u">[] = [
  { id: "wall_graffiti", x: 0, y: 0, r: 0 },
  { id: "wall_lights", x: 4, y: 0, r: 0 },
  { id: "sign_neon", x: 0, y: 2, r: 1 },
  { id: "speaker_black", x: 0, y: 0, r: 0 },
  { id: "djbooth", x: 1, y: 0, r: 0 },
  { id: "speaker_black", x: 3, y: 0, r: 0 },
  { id: "vending_lime", x: 5, y: 0, r: 0 },
  { id: "lava_lime", x: 6, y: 0, r: 0 },
  { id: "sofa_black", x: 0, y: 3, r: 1 },
  { id: "rug_checker", x: 2, y: 3, r: 0 },
  { id: "beanbag_lilac", x: 6, y: 4, r: 0 },
  { id: "plant_lime", x: 0, y: 6, r: 0 },
];
const LINES = ["gm", "fit check", "who's on the decks", "this room goes hard", "pull up", "new kicks", "link's in my post", "turn it up", "nah this is clean", "brb copping a hoodie"];

const pieces = DEMO.map(toFurni).filter((f): f is Furni => !!f);
const blocked = blockedSet(DEMO.map((p, u) => ({ ...p, u })));
const free = (x: number, y: number) => x >= 0 && y >= 0 && x < SIZE && y < SIZE && !blocked.has(y * MAX_GRID + x);
const rnd = <T,>(a: readonly T[]) => a[Math.floor(Math.random() * a.length)];

function swagLook(): Look {
  const pick = (slot: Slot) => rnd(ITEMS.filter((i) => i.slot === slot)).id;
  return { ...randomLook(), hat: Math.random() < 0.6 ? pick("hat") : null, glasses: Math.random() < 0.35 ? pick("glasses") : null };
}

interface Person {
  look: Look;
  x: number; y: number; // tile it's on (or heading to)
  px: number; py: number; // tile it came from
  dir: number;
  moveAt: number;
  steps: number; // steps left in this stroll
  restUntil: number;
  emote: string | null;
  emoteUntil: number;
  says: string | null;
  saysUntil: number;
}

const spots: [number, number][] = [[2, 4], [4, 3], [3, 5], [5, 2]];
const people: Person[] = spots.map(([x, y], i) => ({
  look: swagLook(), x, y, px: x, py: y, dir: i % 2, moveAt: -1e9, steps: 0,
  restUntil: 800 + i * 1400, emote: null, emoteUntil: 0, says: null, saysUntil: 0,
}));

const taken = (x: number, y: number, me: Person) => people.some((p) => p !== me && ((p.x === x && p.y === y) || (p.px === x && p.py === y)));
const DIRS: [number, number, number][] = [[1, 0, 0], [0, 1, 1], [-1, 0, 2], [0, -1, 3]];

function update(now: number) {
  for (const p of people) {
    if (p.emote && now > p.emoteUntil) p.emote = null;
    if (p.says && now > p.saysUntil) p.says = null;
    if (now - p.moveAt < STEP_MS || p.emote) continue;
    p.px = p.x; p.py = p.y;
    if (p.steps > 0) {
      // keep going the same way when possible, so strolls look purposeful
      const opts = DIRS.filter(([dx, dy]) => free(p.x + dx, p.y + dy) && !taken(p.x + dx, p.y + dy, p));
      const ahead = opts.find((d) => d[2] === p.dir);
      const d = ahead && Math.random() < 0.7 ? ahead : opts.length ? rnd(opts) : null;
      p.steps--;
      if (!d) continue;
      p.x += d[0]; p.y += d[1]; p.dir = d[2]; p.moveAt = now;
      if (!p.steps) p.restUntil = now + 1500 + Math.random() * 3500;
    } else if (now > p.restUntil) {
      p.steps = 2 + Math.floor(Math.random() * 4);
      p.dir = Math.floor(Math.random() * 4);
    }
  }
}

// every few seconds someone says something or busts an emote
let nextBeat = 1200;
function beat(now: number) {
  if (now < nextBeat) return;
  nextBeat = now + 1600 + Math.random() * 1800;
  const idle = people.filter((p) => now - p.moveAt > STEP_MS && !p.emote && !p.says);
  if (!idle.length) return;
  const p = rnd(idle);
  if (Math.random() < 0.55) {
    p.says = rnd(LINES.filter((l) => !people.some((q) => q.says === l)));
    p.saysUntil = now + 3200;
  } else {
    const e = rnd(EMOTES);
    p.emote = e.id;
    p.emoteUntil = now + e.ms;
    p.dir = Math.random() < 0.5 ? 0 : 1; // face the camera
  }
}

function bubble(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, alpha: number) {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = "700 13px 'Space Mono', monospace";
  const w = Math.round(ctx.measureText(text).width + 20);
  const x0 = Math.round(x - w / 2), y0 = Math.round(y - 26);
  ctx.fillStyle = "#000";
  ctx.beginPath();
  ctx.roundRect(x0 - 2, y0 - 2, w + 4, 26, 6);
  ctx.fill();
  ctx.fillRect(x - 5, y0 + 22, 10, 6);
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.roundRect(x0, y0, w, 22, 4);
  ctx.fill();
  ctx.fillRect(x - 3, y0 + 21, 6, 4);
  ctx.fillStyle = "#000";
  ctx.textAlign = "center";
  ctx.fillText(text, x, y0 + 15);
  ctx.restore();
}

const canvas = document.getElementById("demo") as HTMLCanvasElement | null;
if (canvas) {
  const ctx = canvas.getContext("2d")!;
  // world box of the room with people in it: walls go up ~110px, heads stick out ~120px above a tile
  const RX = SIZE * 32 + 16, TOP = -150, BOTTOM = SIZE * 32 + 12;
  const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const frame = (now: number) => {
    const W = canvas.clientWidth, H = canvas.clientHeight, dpr = Math.min(2, devicePixelRatio || 1);
    if (canvas.width !== Math.round(W * dpr) || canvas.height !== Math.round(H * dpr)) {
      canvas.width = Math.round(W * dpr);
      canvas.height = Math.round(H * dpr);
    }
    if (!still) { update(now); beat(now); }
    const scale = Math.min(W / (RX * 2), H / (BOTTOM - TOP));
    const ox = W / 2, oy = H / 2 - ((TOP + BOTTOM) / 2) * scale;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale * dpr, 0, 0, scale * dpr, ox * dpr, oy * dpr);
    ctx.imageSmoothingEnabled = false;

    drawFloorAndWalls(ctx, "blackout", "checker", SIZE, null);
    for (const f of pieces) if (isFlat(f)) drawFurni(ctx, f, now);
    const list: Drawable[] = [];
    for (const f of pieces) if (!isFlat(f)) list.push({ ...furniBox(f), key: furniDepth(f), draw: () => drawFurni(ctx, f, now) });
    const talk: (() => void)[] = [];
    for (const p of people) {
      const k = Math.min(1, (now - p.moveAt) / STEP_MS);
      const gx = p.px + (p.x - p.px) * k, gy = p.py + (p.y - p.py) * k;
      const [sx, sy] = iso(gx + 0.5, gy + 0.5);
      const moving = k < 1;
      list.push({
        ...personBox(gx, gy),
        key: gx + gy + 0.1,
        draw: () => {
          ctx.save();
          ctx.translate(sx, sy);
          drawAvatar(ctx, p.look, { dir: p.dir, walk: moving ? k : 0, emote: p.emote, t: now });
          ctx.restore();
        },
      });
      if (p.says) {
        const fade = Math.min(1, (p.saysUntil - now) / 300);
        const text = p.says;
        talk.push(() => bubble(ctx, sx, sy - 128, text, fade));
      }
    }
    drawInDepthOrder(list);
    talk.forEach((t) => t());
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

// ---------- getting in ----------

const enter = document.getElementById("enter") as HTMLButtonElement;
const enterLabel = enter.textContent;
enter.addEventListener("click", async () => {
  enter.disabled = true;
  enter.textContent = "Opening…";
  try {
    const r = await fetch("/api/home", {
      method: "POST",
      headers: { "content-type": "application/json", ...authHeaders() },
      body: JSON.stringify({ token: getToken() }),
    });
    if (!r.ok) throw new Error(await r.text());
    const { id, created } = await r.json();
    location.href = `/r/${id}${created ? "?new=1" : ""}`;
  } catch {
    enter.disabled = false;
    enter.textContent = enterLabel;
    alert("Couldn't open your room. Try again in a moment.");
  }
});

const signin = document.getElementById("signin") as HTMLButtonElement | null;
// playing inside an X post and already signed in there
if (getSession()) document.querySelectorAll(".guest-only").forEach((e) => e.remove());
signin?.addEventListener("click", async () => {
  signin.disabled = true;
  const err = await signInWithX();
  if (err) {
    signin.disabled = false;
    alert(err);
  }
});

// back from X: straight into your room, or say what went wrong
const back = new URLSearchParams(location.search).get("signin");
if (back) history.replaceState(null, "", "/");
if (back === "ok") enter.click();
if (back === "failed") alert("Couldn't sign in with X. Try again.");
